import type { AuthProvider } from "@refinedev/core";

import { getCurrentUser, login, logout } from "@/services/auth";

// ---------------------------------------------------------------- decross iframe 免登
// postMessage 握手消息类型（命名空间 decross:embed-*，双方双向校验 origin）。
// 握手时序：iframe URL 只带非敏感的 embed=decross 与 ticket_issuer（decross origin，
// 须命中 origin 白名单，见 trustedTicketIssuer）；本页面检测到身处 iframe 且无本地
// token 时向 parent 发 embed-auth-request，decross 父页面校验 origin 后建票并回发
// embed-ticket，这里再调 decross 的 exchange 端点换 access_token。凭据全程不进 URL
// （浏览器历史 / 访问日志）。
const MSG_AUTH_REQUEST = "decross:embed-auth-request";
const MSG_TICKET = "decross:embed-ticket";
const EMBED_HANDSHAKE_TIMEOUT_MS = 5000;

// refine 会多次调用 check（StrictMode / 路由切换），握手每页只发起一次
let embedHandshakePromise: Promise<string | null> | null = null;

/** 测试专用：重置握手一次性 Promise */
export function __resetEmbedHandshakeForTest(): void {
  embedHandshakePromise = null;
}

function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // 跨域 iframe 中访问 window.top 可能抛错，此时必然处于嵌入态
    return true;
  }
}

export interface EmbedHandshakeOptions {
  issuerOrigin: string;
  timeoutMs?: number;
  /** 可注入 parent window，便于测试 */
  parent?: Pick<Window, "postMessage">;
}

/**
 * 向 decross 父页面请求一次性 embed ticket。
 * 校验回包 event.origin === issuerOrigin 且消息类型为 decross:embed-ticket；
 * 超时（默认 5s）无响应返回 null，调用方按未登录降级（游客模式 / 登录页）。
 */
export function requestEmbedTicket({
  issuerOrigin,
  timeoutMs = EMBED_HANDSHAKE_TIMEOUT_MS,
  parent = window.parent,
}: EmbedHandshakeOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== issuerOrigin) return;
      const data = event.data as { type?: string; ticket?: unknown } | null;
      if (!data || data.type !== MSG_TICKET || typeof data.ticket !== "string") return;
      cleanup();
      resolve(data.ticket);
    };
    window.addEventListener("message", onMessage);
    parent.postMessage({ type: MSG_AUTH_REQUEST }, issuerOrigin);
  });
}

/** 用一次性 ticket 调 decross 的 exchange 端点换 access_token；失败返回 null */
export async function exchangeEmbedTicket(
  ticket: string,
  issuerBase: string,
): Promise<string | null> {
  try {
    const resp = await fetch(`${issuerBase.replace(/\/$/, "")}/api/auth/embed-ticket/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ticket }),
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { access_token?: string };
    return data.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * 校验并规范化 origin：必须是合法 https origin（开发环境放行
 * http://localhost:* 与 http://127.0.0.1:*）。非法或非白名单协议返回 null。
 */
function normalizeOrigin(raw: string): string | null {
  try {
    const url = new URL(raw);
    const isHttps = url.protocol === "https:";
    const isLocalHttp =
      url.protocol === "http:" && (url.hostname === "localhost" || url.hostname === "127.0.0.1");
    if (!isHttps && !isLocalHttp) return null;
    return url.origin;
  } catch {
    return null;
  }
}

// decross origin 白名单：VITE_DECROSS_API_URL 的 origin，外加可选
// VITE_DECROSS_ALLOWED_ORIGINS（逗号分隔的 origin 列表）。
function allowedDecrossOrigins(): Set<string> {
  const origins = new Set<string>();
  const apiUrl = import.meta.env.VITE_DECROSS_API_URL as string | undefined;
  if (apiUrl) {
    const origin = normalizeOrigin(apiUrl);
    if (origin) origins.add(origin);
  }
  const extra = import.meta.env.VITE_DECROSS_ALLOWED_ORIGINS as string | undefined;
  if (extra) {
    for (const item of extra.split(",")) {
      const origin = normalizeOrigin(item.trim());
      if (origin) origins.add(origin);
    }
  }
  return origins;
}

/**
 * URL 参数 ticket_issuer 仅当其 origin 命中白名单时才可信，返回规范化 origin；
 * 否则（含未配置任何 env 的情况）返回 null，完全忽略该参数——否则攻击者构造
 * ?embed=decross&ticket_issuer=https://evil.com 链接即可让受害者浏览器向
 * 攻击者域发握手并交换"token"写入 localStorage（会话固定）。
 */
function trustedTicketIssuer(ticketIssuer: string | null): string | null {
  if (!ticketIssuer) return null;
  const origin = normalizeOrigin(ticketIssuer);
  if (!origin || !allowedDecrossOrigins().has(origin)) return null;
  return origin;
}

/**
 * exchange 端点的 base URL：优先取开发者在构建期配置的
 * VITE_DECROSS_API_URL（可信来源，原样使用），否则取已命中白名单的 issuer。
 */
function decrossApiBase(trustedIssuer: string | null): string | null {
  return (import.meta.env.VITE_DECROSS_API_URL as string | undefined) || trustedIssuer;
}

export const authProvider: AuthProvider = {
  login: async ({ username, password }) => {
    await login(String(username), String(password));
    return {
      success: true,
      redirectTo: "/",
    };
  },
  logout: async () => {
    await logout();
    return {
      success: true,
      redirectTo: "/login",
    };
  },
  check: async () => {
    let token = localStorage.getItem("access_token");
    if (!token && typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);

      // URL 不接受任何凭据参数：?ticket= 兼容路径已随 postMessage 握手全量
      // 覆盖而移除（decross 侧 grep 确认不再产生此类 URL），?access_token=
      // 直传更早移除——两者都是会话固定向量：攻击者可构造带自己凭据的链接
      // 让受害者登录进攻击者会话。URL 上唯一被读取的参数是非敏感的
      // ticket_issuer（decross origin），且必须命中 origin 白名单才可信
      // （见 trustedTicketIssuer），否则完全忽略。
      const trustedIssuer = trustedTicketIssuer(params.get("ticket_issuer"));

      // postMessage 握手（首选路径）：身处 iframe、URL 带非敏感 ticket_issuer
      // （decross origin，须命中白名单）且无本地 token 时，向 decross 父页面
      // 请求一次性 ticket，凭据全程不进 URL。5s 无响应或交换失败按未登录处理
      // （走下方登录跳转）。
      if (!token && trustedIssuer && isEmbedded()) {
        embedHandshakePromise ??= requestEmbedTicket({ issuerOrigin: trustedIssuer });
        const handshakeTicket = await embedHandshakePromise;
        if (handshakeTicket) {
          const issuerBase = decrossApiBase(trustedIssuer);
          if (issuerBase) {
            token = await exchangeEmbedTicket(handshakeTicket, issuerBase);
            if (token) {
              localStorage.setItem("access_token", token);
            }
          }
        }
      }
    }
    if (!token) {
      return {
        authenticated: false,
        redirectTo: "/login",
      };
    }
    try {
      await getCurrentUser();
      return {
        authenticated: true,
      };
    } catch {
      logout();
      return {
        authenticated: false,
        redirectTo: "/login",
      };
    }
  },
  getPermissions: async () => {
    try {
      const user = await getCurrentUser();
      return user.roles;
    } catch {
      return [];
    }
  },
  getIdentity: async () => {
    try {
      const user = await getCurrentUser();
      return {
        id: user.id,
        name: user.name,
        avatar: undefined,
      };
    } catch {
      return null;
    }
  },
  onError: async (error) => {
    if (error?.status === 401) {
      logout();
      return {
        logout: true,
        redirectTo: "/login",
      };
    }
    return {};
  },
};
